import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Style from './Governance.module.css';
import { getScanRules } from '../../store/entities/dataControlEntity';
import {
  getActionLookupData,
  getGovCategoryLookupData,
} from '../../store/entities/lookup';

// Fallback used while lookup data is loading.
const FALLBACK_CATEGORIES = [
  { code: 'FINANCIAL', value: 'Financial' },
  { code: 'GOVERNMENT_ID', value: 'Government IDs' },
  { code: 'CREDENTIALS', value: 'Credentials & Secrets' },
  { code: 'PII', value: 'PII' },
  { code: 'CUSTOM', value: 'Custom' },
];

// family: 'SENSITIVE_DATA' | 'FILE_TRANSFER'
// rule:   existing rule object (edit) or null (add)
// onSave(payload, ruleId|null), onClose()
const AddEditRule = ({
  family, rule, onSave, onClose, saving,
  scanRules, getScanRules,
  actions, getActionLookupData,
  govCategories, getGovCategoryLookupData,
}) => {
  const isEdit = !!rule;
  const isSensitive = family === 'SENSITIVE_DATA';

  // DEFAULT (built-in) rules are definition-locked: the name / category /
  // scan-rule stay disabled and only the action + enabled state are editable —
  // the API enforces the same restriction. (Governance is PixmAdmin-only.)
  const isDefault = isEdit && rule.sourceCode === 'DEFAULT';

  useEffect(() => {
    getScanRules();
    getActionLookupData();
    if (isSensitive) getGovCategoryLookupData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = govCategories && govCategories.length > 0 ? govCategories : FALLBACK_CATEGORIES;

  // Partition scan rules by family: SR_FT_* are FILE_TRANSFER rules, all others are SENSITIVE_DATA.
  const ftScanRules        = (scanRules || []).filter((sr) => sr.code && sr.code.startsWith('SR_FT_'));
  const sensitiveScanRules = (scanRules || []).filter((sr) => !sr.code || !sr.code.startsWith('SR_FT_'));
  const relevantScanRules  = isSensitive ? sensitiveScanRules : ftScanRules;

  const [scanRuleId, setScanRuleId]     = useState(rule && rule.scanRuleId != null ? String(rule.scanRuleId) : '');
  const [ruleDescription, setRuleDescription] = useState(rule?.ruleDescription || '');
  const [categoryCode, setCategoryCode] = useState(rule?.categoryCode || 'FINANCIAL');
  const [actionCode, setActionCode]     = useState(rule?.actionCode || 'REPORT');
  const [isActive, setIsActive]         = useState(rule?.isActive !== false);
  const [errors, setErrors]             = useState([]);

  const validate = () => {
    const e = [];
    if (!ruleDescription.trim()) e.push('Rule name is required');
    if (ruleDescription.length > 200) e.push('Rule name cannot exceed 200 characters');
    if (!scanRuleId) e.push(isSensitive ? 'Select a scan rule' : 'Select a file transfer rule');
    return e;
  };

  const handleSave = () => {
    const e = validate();
    setErrors(e);
    if (e.length) return;

    const base = {
      ruleFamilyCode: family,
      ruleDescription,
      scanRuleId: Number(scanRuleId),
      actionCode,
      isActive,
    };
    const payload = isSensitive ? { ...base, categoryCode } : base;
    onSave(payload, rule ?? null);
  };

  const title = (isEdit ? 'Edit ' : 'Add ') + (isSensitive ? 'Detection Rule' : 'Transfer Rule');

  return (
    <div className={Style.modalOverlay} onMouseDown={onClose}>
      <div className={Style.modal} onMouseDown={(ev) => ev.stopPropagation()}>
        <div className={Style.modalHeader}>{title}</div>
        <div className={Style.modalBody}>
          {errors.length > 0 && (
            <ul className={Style.errorList}>
              {errors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          )}
          {isDefault && (
            <div className={Style.hint}>
              This is a built-in rule — its definition is locked. You can change the action and
              enable/disable it.
            </div>
          )}

          <div className={Style.formGroup}>
            <label className={Style.label}>Rule Name</label>
            <input
              className={Style.input}
              value={ruleDescription}
              disabled={isDefault}
              onChange={(e) => setRuleDescription(e.target.value)}
              placeholder={isSensitive ? 'e.g. Internal project codenames' : 'e.g. No executable uploads'}
            />
          </div>

          {isSensitive && (
            <div className={Style.formGroup}>
              <label className={Style.label}>Category</label>
              <select className={Style.select} value={categoryCode} disabled={isDefault}
                onChange={(e) => setCategoryCode(e.target.value)}>
                {categories.map((c) => <option key={c.code} value={c.code}>{c.value}</option>)}
              </select>
            </div>
          )}

          <div className={Style.formGroup}>
            <label className={Style.label}>
              {isSensitive ? 'Detection Scan Rule' : 'File Transfer Rule'}
            </label>
            <div className={Style.hint}>
              {isSensitive
                ? 'What this rule detects is defined by the scan rule you select below — no pattern is entered here.'
                : 'Select one of the predefined file transfer rules. The rule defines exactly what is detected (file type, size, or name pattern).'}
            </div>
            <select className={Style.select} value={scanRuleId} disabled={isDefault}
              onChange={(e) => setScanRuleId(e.target.value)}>
              <option value="" disabled>
                {isSensitive ? 'Select a scan rule…' : 'Select a file transfer rule…'}
              </option>
              {relevantScanRules.map((sr) => (
                <option key={sr.scanRuleId} value={sr.scanRuleId}>{sr.name}</option>
              ))}
            </select>
            {relevantScanRules.length === 0 && (
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                No rules available — they are seeded by the platform.
              </div>
            )}
          </div>

          <div className={Style.formGroup}>
            <label className={Style.label}>Action</label>
            <select className={Style.select} value={actionCode}
              onChange={(e) => setActionCode(e.target.value)}>
              {(actions || []).length === 0 && <option value={actionCode}>{actionCode}</option>}
              {(actions || []).map((a) => <option key={a.code} value={a.code}>{a.value}</option>)}
            </select>
          </div>

          <div className={Style.formGroup}>
            <label className={Style.label} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              Rule enabled
            </label>
          </div>
        </div>
        <div className={Style.modalFooter}>
          <button className={Style.btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  scanRules: state.entities.dataControlEntity.scanRules,
  actions: state.entities.lookup.actions,
  govCategories: state.entities.lookup.govCategories,
});
const mapDispatchToProps = (dispatch) => ({
  getScanRules: () => dispatch(getScanRules()),
  getActionLookupData: () => dispatch(getActionLookupData()),
  getGovCategoryLookupData: () => dispatch(getGovCategoryLookupData()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddEditRule);
